/**
 * platform-ai-catalog-page.tsx — Platform → AI → Danh mục model AI (`/platform/ai/catalog`).
 * Model nào bán dưới dạng "AI của eCDP", mở cho gói nào (Free / chỉ trả phí), giá MUA VÀO USD / 1M token, hạng.
 * Cạnh giá mua vào là GIÁ BÁN RA / 1M token (đ) = giá mua vào × tỷ giá × (1 + biên %) — server tính theo Cài đặt token.
 */
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Boxes, Loader2, Plus, Save } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { EmptyState, ErrorState } from '@/components/shared/feedback'
import { AiProviderOption } from '@/components/shared/ai-provider-option'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { SUPPORTED_AI_PROVIDERS } from '@/lib/ai-providers'
import { aiErrorMessage, formatPricePer1M } from '@/lib/ai-credits'
import {
  useCreateCatalogModel, usePlatformAiCatalog, useUpdateCatalogModel,
  type CatalogModel, type CatalogPatch, type MinPlan,
} from '@/hooks/use-platform-ai'
import { providerName } from './platform-ai-keys-page'
import { catalogDraftError, TIER_OPTIONS } from './platform-ai-lib'

const rowKey = (m: Pick<CatalogModel, 'provider' | 'model'>) => `${m.provider}:${m.model}`
const MIN_PLAN_LABEL: Record<MinPlan, string> = { free: 'Mọi gói (kể cả Free)', paid: 'Chỉ gói trả phí' }

export function PlatformAiCatalogPage() {
  const { data, isLoading, isError } = usePlatformAiCatalog()
  const update = useUpdateCatalogModel()
  const [provider, setProvider] = useState<string>('all')
  const [drafts, setDrafts] = useState<Record<string, { priceIn: string; priceOut: string; tier: string; title: string }>>({})
  const [adding, setAdding] = useState(false)

  const rows = useMemo(
    () => (data ?? []).filter((m) => provider === 'all' || m.provider === provider),
    [data, provider],
  )
  const onErr = (e: unknown) => toast.error(aiErrorMessage(e))
  const patch = (m: CatalogModel, p: CatalogPatch, msg: string) =>
    update.mutate({ provider: m.provider, model: m.model, patch: p }, { onSuccess: () => toast.success(msg), onError: onErr })

  const draftOf = (m: CatalogModel) =>
    drafts[rowKey(m)] ?? { priceIn: String(m.priceIn), priceOut: String(m.priceOut), tier: m.tier ?? '', title: m.title }
  const setDraft = (m: CatalogModel, p: Partial<ReturnType<typeof draftOf>>) =>
    setDrafts((d) => ({ ...d, [rowKey(m)]: { ...draftOf(m), ...p } }))
  const dirty = (m: CatalogModel) => {
    const d = drafts[rowKey(m)]
    return !!d && (d.priceIn !== String(m.priceIn) || d.priceOut !== String(m.priceOut) || d.tier !== (m.tier ?? '') || d.title !== m.title)
  }
  const saveRow = (m: CatalogModel) => {
    const d = draftOf(m)
    const err = catalogDraftError(d)
    if (err) return void toast.error(err)
    update.mutate(
      { provider: m.provider, model: m.model, patch: { title: d.title.trim(), tier: d.tier || null, priceIn: Number(d.priceIn), priceOut: Number(d.priceOut) } },
      {
        onSuccess: () => {
          toast.success(`Đã lưu ${d.title.trim()}`)
          setDrafts((cur) => { const n = { ...cur }; delete n[rowKey(m)]; return n })
        },
        onError: onErr,
      },
    )
  }

  const columns: Column<CatalogModel>[] = [
    { key: 'provider', header: 'Nhà cung cấp', cell: (m) => <AiProviderOption id={m.provider} name={providerName(m.provider).split(' (')[0]} /> },
    {
      key: 'model', header: 'Model',
      cell: (m) => (
        <div className="min-w-44 space-y-1">
          <Input className="h-8" value={draftOf(m).title} onChange={(e) => setDraft(m, { title: e.target.value })} aria-label="Tên hiển thị" />
          <p className="font-mono text-xs text-muted-foreground">{m.model}{m.kind === 'embedding' ? ' · nhúng' : ''}</p>
        </div>
      ),
    },
    {
      key: 'tier', header: 'Hạng',
      cell: (m) => (
        <select
          className="h-8 rounded-md border bg-transparent px-2 text-sm dark:bg-card"
          value={draftOf(m).tier}
          onChange={(e) => setDraft(m, { tier: e.target.value })}
          aria-label="Hạng model"
        >
          <option value="">—</option>
          {TIER_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          {m.tier && !TIER_OPTIONS.some((t) => t.id === m.tier) && <option value={m.tier}>{m.tier}</option>}
        </select>
      ),
    },
    {
      key: 'priceIn', header: 'Mua vào $/1M (vào)', align: 'right',
      cell: (m) => <Input className="h-8 w-24 text-right" type="number" min={0} step="0.01" value={draftOf(m).priceIn} onChange={(e) => setDraft(m, { priceIn: e.target.value })} aria-label="Giá vào" />,
    },
    {
      key: 'priceOut', header: 'Mua vào $/1M (ra)', align: 'right',
      cell: (m) => <Input className="h-8 w-24 text-right" type="number" min={0} step="0.01" value={draftOf(m).priceOut} onChange={(e) => setDraft(m, { priceOut: e.target.value })} aria-label="Giá ra" />,
    },
    {
      key: 'sale', header: 'Bán ra đ / 1M (vào · ra)', align: 'right',
      cell: (m) => m.salePricePer1M ? (
        <span className="whitespace-nowrap tabular-nums">
          <b>{formatPricePer1M(m.salePricePer1M.inVnd)}</b>
          <span className="text-muted-foreground"> · </span>
          <b>{formatPricePer1M(m.salePricePer1M.outVnd)}</b>
          {m.isReference && <span className="block text-[10px] font-normal text-muted-foreground">model tham chiếu</span>}
        </span>
      ) : '—',
    },
    {
      key: 'minPlan', header: 'Mở cho',
      cell: (m) => (
        <select
          className="h-8 rounded-md border bg-transparent px-2 text-sm dark:bg-card"
          value={m.minPlan}
          onChange={(e) => patch(m, { minPlan: e.target.value as MinPlan }, `${m.title}: ${MIN_PLAN_LABEL[e.target.value as MinPlan]}`)}
          aria-label="Mở cho gói"
        >
          <option value="free">{MIN_PLAN_LABEL.free}</option>
          <option value="paid">{MIN_PLAN_LABEL.paid}</option>
        </select>
      ),
    },
    {
      key: 'managed', header: 'Mở bán', align: 'center',
      cell: (m) => (
        <Switch
          checked={m.managedEnabled}
          aria-label="Mở bán"
          onCheckedChange={(v) => patch(m, { managedEnabled: v }, v ? `Đã mở bán ${m.title}` : `Đã tắt bán ${m.title}`)}
        />
      ),
    },
    {
      key: 'save', header: '', align: 'right',
      cell: (m) => dirty(m) ? (
        <Button size="sm" onClick={() => saveRow(m)} disabled={update.isPending}><Save className="h-3.5 w-3.5" /> Lưu</Button>
      ) : null,
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Danh mục model AI"
        description="Model bán dưới dạng “AI của eCDP”, gói được dùng và giá gốc. Tắt “Mở bán” hoặc đặt “Chỉ gói trả phí” → model hiện 🔒 ở màn Cấu hình và form nhân viên AI của khách."
        actions={data !== null && <Button onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> Thêm model</Button>}
      />
      {isError ? (
        <ErrorState />
      ) : data === null ? (
        <EmptyState icon={Boxes} title="Máy chủ chưa hỗ trợ danh mục model AI" description="Tính năng AI hai chế độ đang được triển khai phía máy chủ." />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả nhà cung cấp</SelectItem>
                {SUPPORTED_AI_PROVIDERS.map((p) => <SelectItem key={p} value={p}>{providerName(p)}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Token / lượt ước tính cho một lượt ~3.000 token vào + 400 token ra (tính theo nhà cung cấp), tính lại khi đổi giá hoặc Cài đặt token.
            </p>
          </div>
          <DataTable columns={columns} rows={rows} rowKey={rowKey} loading={isLoading} emptyTitle="Chưa có model nào" />
        </>
      )}
      <AddModelDialog open={adding} onClose={() => setAdding(false)} />
    </div>
  )
}

function AddModelDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateCatalogModel()
  const empty = { provider: 'openai', model: '', title: '', kind: 'chat', tier: 'standard', priceIn: '', priceOut: '', minPlan: 'paid' as MinPlan, managedEnabled: false }
  const [f, setF] = useState(empty)
  const set = (p: Partial<typeof f>) => setF((cur) => ({ ...cur, ...p }))
  const close = () => { setF(empty); onClose() }
  const save = () => {
    if (!f.model.trim()) return void toast.error('Nhập mã model')
    const err = catalogDraftError({ title: f.title || f.model, priceIn: f.priceIn, priceOut: f.priceOut, tier: f.tier })
    if (err) return void toast.error(err)
    create.mutate(
      {
        provider: f.provider, model: f.model.trim(), title: (f.title || f.model).trim(), kind: f.kind, tier: f.tier || null,
        priceIn: Number(f.priceIn), priceOut: Number(f.priceOut), priceCacheRead: null, priceCacheWrite: null, dims: null,
        managedEnabled: f.managedEnabled, minPlan: f.minPlan,
      },
      { onSuccess: () => { toast.success('Đã thêm model'); close() }, onError: (e) => toast.error(aiErrorMessage(e)) },
    )
  }
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Thêm model vào danh mục</DialogTitle>
          <DialogDescription>Model mới mặc định chưa mở bán — kiểm tra giá rồi bật “Mở bán”.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Nhà cung cấp</Label>
            <Select value={f.provider} onValueChange={(v) => set({ provider: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUPPORTED_AI_PROVIDERS.map((p) => <SelectItem key={p} value={p}>{providerName(p)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Loại</Label>
            <Select value={f.kind} onValueChange={(v) => set({ kind: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="chat">Hội thoại (chat)</SelectItem>
                <SelectItem value="embedding">Nhúng tài liệu (embedding)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Mã model *</Label>
            <Input value={f.model} onChange={(e) => set({ model: e.target.value })} placeholder="vd: gpt-5.6-mini" className="font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label>Tên hiển thị</Label>
            <Input value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="vd: GPT-5.6 Mini" />
          </div>
          <div className="space-y-1.5">
            <Label>Giá vào ($ / 1M token) *</Label>
            <Input type="number" min={0} step="0.01" value={f.priceIn} onChange={(e) => set({ priceIn: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Giá ra ($ / 1M token) *</Label>
            <Input type="number" min={0} step="0.01" value={f.priceOut} onChange={(e) => set({ priceOut: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Hạng</Label>
            <Select value={f.tier} onValueChange={(v) => set({ tier: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TIER_OPTIONS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Mở cho</Label>
            <Select value={f.minPlan} onValueChange={(v) => set({ minPlan: v as MinPlan })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="free">{MIN_PLAN_LABEL.free}</SelectItem>
                <SelectItem value="paid">{MIN_PLAN_LABEL.paid}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <Switch checked={f.managedEnabled} onCheckedChange={(v) => set({ managedEnabled: v })} /> Mở bán ngay
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>Huỷ</Button>
          <Button onClick={save} disabled={create.isPending}>{create.isPending && <Loader2 className="animate-spin" />} Thêm model</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
